import {forwardRef,useEffect,useState,useCallback} from "react";
import {Box,Stack,ButtonGroup,Button} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {styled} from '@mui/material/styles';
import {useSnackbar} from '../SnackbarContext.js';
import {useLoading} from '../LoadingContext';
import {consoleLog} from '../../utilities/Utilities';
import {getObjectRecords,createAttachment,deleteObjectRecordsByIdList,getAttachment} from '../../integration/ObjectManager.js';
import ImageItem from './ImageItem.js';
import {useConfirm} from '../ConfirmContext.js';
import Popup from './Popup.js';

const VisuallyHiddenInput=styled('input')({
    clip:'rect(0 0 0 0)',
    clipPath:'inset(50%)',
    height:1,
    overflow:'hidden',
    position:'absolute',
    bottom:0,
    left:0,
    whiteSpace:'nowrap',
    width:1,
});

const ImageTab=forwardRef(({parentId,parentTable,parent,refreshToken,application,userData,noImage,entityParams,file,height,executeQuery={current:true}},ref)=>{
    const [images,setImages]=useState([]);
    const [entityRules,setEntityRules]=useState({});
    const {showSnackbar}=useSnackbar();
    const {showLoading,hideLoading}=useLoading();
    const {confirm}=useConfirm();
    const [openPopup,setOpenPopup]=useState(false);
    const [selectedIndex,setSelectedIndex]=useState(null);
    const [imageSrc,setImageSrc]=useState(null);

    const getRecords=useCallback(async (isSubscribed=true)=>{
        showLoading();
        const data=await getObjectRecords({entityType:'Attachment',entityParams});
        if(!isSubscribed) return;
        consoleLog({file:file,context:'getRecords - getObjectRecords',arg_name:'data',arg_value:data});
        if ('error' in data) {
            showSnackbar(data.error,'error');
            setImages([]);
        } 
        else {
            setImages(data.records||[]);
            setEntityRules(data.entity_rules);
        }
        hideLoading();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[parentId,entityParams]);

    useEffect(()=>{
        let isSubscribed=true;
        if(parentId){
            executeQuery.current=true;
            if(executeQuery.current){
                getRecords(isSubscribed);
                executeQuery.current=false;
            }
        }
        else{
            executeQuery.current=true;
        }
        return()=>{
            isSubscribed=false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[parentId,getRecords]);

    const handleDeleteImage=async(item)=>{
        if(await confirm({message:application.getLocale("ConfirmImageDeletion")})){
            handleProcessDeleteImage(item);
        }
    }
    
    const handleProcessDeleteImage=(item)=>{
        const idList=[item.id];
        consoleLog({file:file,context:'handleDeleteImage',arg_name:'idList',arg_value:idList});
        showLoading();
        deleteObjectRecordsByIdList({entityType:'Attachment',entityParams:['idList='+idList]})
        .then((data)=>{
            consoleLog({file:file,context:'handleDeleteImage - deleteObjectRecordsByIdList',arg_name:'data',arg_value:data});
            if('error' in data){
                showSnackbar(data.error,'error');
            }
            else{
                showSnackbar(application.getLocale("ImageRemovedWithSuccess"),'success');
                getRecords();
            }
            hideLoading();
        });
    }

    const handleClickAddImage=(event)=>{
        showLoading();
        const file=event.target.files[0];
        if(file){
            if(!file.type.startsWith("image/")) {
                showSnackbar(application.getLocale("ErrorMsgFileTypeNotSupported"),'error');
                hideLoading();
                return;
            }
            const MAX_FILE_SIZE=2*1024*1024;//Max Limit of 2Mb
            if(file.size>MAX_FILE_SIZE){
                showSnackbar(application.getLocale("ErrorMsgFileSizeExceedsLimit"),'error');
                hideLoading();
                return;
            }

            const formData=new FormData();
            formData.append('parent_id',parentId);
            formData.append('table_name',parentTable);
            formData.append('file',file);
            
            createAttachment(formData)
            .then((data)=>{
                if('error' in data){
                    showSnackbar(data.error,'error');
                    hideLoading();
                }
                else{
                    showSnackbar(application.getLocale("MsgImageAddedWithSuccess"),'success');
                    getRecords();
                }
            });
        }
    }

    const closePreviewPopup=()=>{
        setOpenPopup(false);
    }

    const openPreviewPopup=async(image,index)=>{
        const data=await getAttachment(image.id);
        if(!data.error){
            setImageSrc(data.src);
            setSelectedIndex(index);
            setOpenPopup(true);
        }
    };

    
    

    const isLast=selectedIndex===images.length-1;
    const goNext=async()=>{
        if(isLast) return;
        const nextIndex=selectedIndex+1;
        const data=await getAttachment(images[nextIndex].id);
        if(!data.error){
            setImageSrc(data.src);
            setSelectedIndex(nextIndex);
        }
    };

    const isFirst=selectedIndex===0;
    const goPrev=async()=>{
        if(isFirst) return;
        const prevIndex=selectedIndex-1;
        const data= await getAttachment(images[prevIndex].id);
        if(!data.error){
            setImageSrc(data.src);
            setSelectedIndex(prevIndex);
        }
    };

    const previewPopupFooterComponent=(
        <Box
            sx={{
                display:'flex',
                justifyContent:'center',
                alignItems:'center',
                gap: 2,
                mt: 2}}>
            <Button
                onClick={goPrev}
                disabled={isFirst}
                variant="outlined">◀ {application.getLocale("ButtonPrevious")}
            </Button>
            <Button
                onClick={goNext}
                disabled={isLast}
                variant="outlined">{application.getLocale("ButtonNext")} ▶
            </Button>
        </Box>
    );

    const previewPopup=(
        <Popup
            title={application.getLocale("TitleImage")}
            footerComponent={previewPopupFooterComponent}
            onClose={()=>closePreviewPopup()}>
            <Box
                sx={{
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'center',
                }}>
                <img
                    src={imageSrc}
                    alt={application.getLocale("TitleImage")}
                    style={{
                        maxWidth:'100%',
                        maxHeight:'70vh',
                        objectFit:'contain'
                    }}/>
            </Box>
        </Popup>
    );

    const renderThumbs=(
        <div className="page-list">
            <div className="page-list-content">
                <ButtonGroup size="small" sx={{minHeight:24,mt:1,ml:2}}>
                    {entityRules?.can_create&&(
                        <Button component="label" role={undefined} tabIndex={-1} name="add" variant="outlined" startIcon={<FileUploadIcon />}>{application.getLocale("ButtonAddImage")}
                            <VisuallyHiddenInput
                                type="file"
                                accept="image/*"
                                onChange={handleClickAddImage}
                                multiple={false}
                            />
                        </Button>)}
                </ButtonGroup>
                <Stack direction={{xs:'column',sm:'row'}} spacing={{xs:1,sm:2,md:2}}>
                    <div style={{height:height,width:'100%'}}>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 2,
                                p: 2,
                            }}>
                            {images.map((img,index)=>(
                                <ImageItem key={img.id} index={index} img={img} noImage={noImage} application={application} handleDeleteImage={handleDeleteImage} openPreviewPopup={openPreviewPopup}/>
                            ))}
                        </Box>
                    </div>
                </Stack>
            </div>
        </div>
    )

    return (openPopup?previewPopup:renderThumbs);
});

export default ImageTab;
